import React from "react";
import { useParams, Navigate } from "react-router-dom";
import TitleButtons from "../../../../components/TitleButtons";
import HistoricoWidget, {
  HistoricoItem,
} from "../../../../components/tableHistorico";
import HistoricoPrecosWidget from "../../../../components/tablePrice";
import HistoricoPrecosSellWidget from "../../../../components/tablePriceSell";
import { useAuth } from "../../../../context/auth-context";

// Mantém a API original de produtos/estoque.
// O useAuth continua sendo usado apenas para sessão.
const PRODUTOS_API_BASE = "https://api.eva-googlemarine.com";

// ====================== Tipos de API ======================
type ApiProduto = {
  id: number;
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  vendavel: boolean;
  responsavel_registro: number | null;
  departamento: string;
  criado_em: string;
  atualizado_em: string;
  dimensao?: string | null;
  peso?: string | null;
  ocultar?: boolean | null;
};

type ApiMovItem = HistoricoItem & {
  moeda?: string | null;
  tipo_full?: string | null;
  custo?: string | number | null;
  custo_unitario?: string | number | null;
};

type MovimentosPorTipo = {
  entrada?: ApiMovItem[];
  saida?: ApiMovItem[];
  perda?: ApiMovItem[];
  venda?: ApiMovItem[];
  consumo?: ApiMovItem[];
  retorno?: ApiMovItem[];
  standby?: ApiMovItem[];
  [k: string]: ApiMovItem[] | undefined;
};

type ApiEstoqueAtual = {
  produto_id: number;
  quantidade_atual: number | null;
  custo_unitario_atual: string | number | null;
  moeda?: string | null;
};

type ApiPrecoVendaEntry = {
  id: number;
  data_definicao: string;
  definido_por_id: number;
  definido_por_nome: string;
  margem_percentual: number;
  preco_venda: number | string;
};

type ApiResponse = {
  ok: boolean;
  oculto?: boolean;
  data?: {
    produto: ApiProduto;
    custos: ApiMovItem[];
    movimentos_por_tipo?: MovimentosPorTipo;
    estoque_atual: ApiEstoqueAtual | null;
    precos_venda?: ApiPrecoVendaEntry[];
    preco_venda?: number | string | null;
  };
  erro?: string;
};

type SellHistItem = {
  id: number;
  produto_id: number;
  movimentacao_id?: number;
  tipo?: string | null;
  custo_unitario: number | null;
  quantidade: number | null;
  data_movimentacao: string | null;
  responsavel_id: number | null;
  responsavel_nome?: string | null;
  margem_mov_percentual?: number | null;
  margem_percentual?: number | null;
  preco_venda?: number | null;
  custo_referencia?: number | null;
  moeda?: string | null;
};

// ====================== Helpers ======================
const toPositiveIntOrNull = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;

  const n = typeof value === "number" ? value : Number(String(value).trim());

  return Number.isInteger(n) && n > 0 ? n : null;
};

const currencySymbol = (code?: string | null): string => {
  const c = String(code || "")
    .trim()
    .toUpperCase();

  if (c === "BRL") return "R$";
  if (c === "USD") return "$";
  if (c === "EUR") return "€";

  return "";
};

const parseNumberFlexible = (val: unknown): number => {
  if (val == null) return NaN;
  if (typeof val === "number") return val;

  let s = String(val)
    .trim()
    .replace(/[\s\u00A0\u202F]/g, "");

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");

  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");

    s =
      lastComma > lastDot
        ? s.replace(/\./g, "").replace(",", ".")
        : s.replace(/,/g, "");
  } else if (hasComma) {
    s = s.replace(",", ".");
  }

  const n = Number(s);

  return Number.isFinite(n) ? n : NaN;
};

const formatMoneyByCode = (
  value: number | string | null | undefined,
  code?: string | null
): string => {
  if (value === null || value === undefined || value === "") return "—";

  const n = parseNumberFlexible(value);

  if (!Number.isFinite(n)) return "—";

  const sym = currencySymbol(code);

  const num = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  return sym ? `${sym} ${num}` : num;
};

const lc = (s?: string | null) =>
  String(s ?? "")
    .trim()
    .toLowerCase();

const isAjustePreco = (item: any) => {
  const t1 = lc(item?.tipo);
  const t2 = lc(item?.tipo_full);

  return (
    t1 === "ajuste-preco" ||
    t1 === "ajuste_preco" ||
    t1 === "ajuste preço" ||
    t2 === "ajuste-preco" ||
    t2 === "ajuste_preco" ||
    t2 === "ajuste preço"
  );
};

const isAjusteGenerico = (item: any) => {
  const t1 = lc(item?.tipo);
  const t2 = lc(item?.tipo_full);

  return t1 === "ajuste" || t2 === "ajuste";
};

const withCustoFields = (
  item: any,
  moedaPadrao?: string | null,
  nomeTipo?: string
): ApiMovItem => {
  const moedaItem = item?.moeda ?? moedaPadrao ?? "BRL";

  const raw =
    item?.custo_unitario ??
    item?.custo ??
    item?.preco ??
    item?.valor ??
    item?.preco_unitario ??
    null;

  const num = parseNumberFlexible(raw);

  return {
    ...item,
    ...(nomeTipo ? { tipo: nomeTipo } : {}),
    custo_unitario: Number.isFinite(num) ? num : null,
    custo: Number.isFinite(num) ? num : null,
    moeda: moedaItem,
  };
};

// ====================== Página ======================
const DetalhesProdutoDiretoria: React.FC = () => {
  const { codigo: codigoParam } = useParams<{ codigo: string }>();
  const codigo = decodeURIComponent(codigoParam ?? "");

  const { sessao, loading: authLoading, refresh: refreshAuth } = useAuth();

  const responsavelId = React.useMemo(() => {
    return (
      toPositiveIntOrNull(sessao?.id) ??
      toPositiveIntOrNull(sessao?.uid) ??
      toPositiveIntOrNull(sessao?.solicitante_id) ??
      toPositiveIntOrNull(sessao?.conta_id)
    );
  }, [sessao]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<ApiResponse["data"] | null>(
    null
  );
  const [isOculto, setIsOculto] = React.useState<boolean>(false);

  const [activeTab, setActiveTab] = React.useState<
    "mov" | "precosCompra" | "precosVenda"
  >("mov");

  const [showMargin, setShowMargin] = React.useState(false);
  const [marginInput, setMarginInput] = React.useState<string>("");
  const [priceInput, setPriceInput] = React.useState<string>("");
  const [marginPosting, setMarginPosting] = React.useState(false);
  const [marginError, setMarginError] = React.useState<string | null>(null);
  const [marginSuccess, setMarginSuccess] = React.useState<string | null>(null);

  const [vendavelPosting, setVendavelPosting] = React.useState(false);
  const [vendavelMsg, setVendavelMsg] = React.useState<string | null>(null);
  const [vendavelErr, setVendavelErr] = React.useState<string | null>(null);

  const [isNarrow, setIsNarrow] = React.useState<boolean>(
    () => window.innerWidth < 920
  );

  React.useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 920);

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  const formatCurrency = (
    v: string | number | null | undefined,
    currency?: "BRL" | "USD" | "EUR" | null
  ) => {
    if (v === null || v === undefined || v === "") return "—";

    const n = typeof v === "number" ? v : parseNumberFlexible(v);

    if (!Number.isFinite(n)) return "—";

    const cur = (currency || "BRL") as "BRL" | "USD" | "EUR";

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: cur,
    }).format(n);
  };

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return "—";

    const m = iso.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
    );

    if (!m) return iso || "—";

    const [, ano, mes, dia, hora, minuto] = m;

    return `${dia}/${mes}/${ano} - ${hora}:${minuto}`;
  };

  const fetchProduto = React.useCallback(
    async (signal?: AbortSignal) => {
      if (!codigo) return;

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${PRODUTOS_API_BASE}/departamentos/diretoria/produtos/${encodeURIComponent(
            codigo
          )}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            cache: "no-store",
            signal,
          }
        );

        const txt = await res.text().catch(() => "");

        let json: ApiResponse | null = null;

        try {
          json = txt ? JSON.parse(txt) : null;
        } catch {
          json = null;
        }

        if (!res.ok) {
          throw new Error(
            json?.erro ||
              `Falha ao buscar produto (HTTP ${res.status}) ${txt || ""}`.trim()
          );
        }

        if (!json?.ok || !json.data) {
          throw new Error(json?.erro || "Resposta inválida da API");
        }

        setPayload(json.data);
        setIsOculto(
          Boolean(json.oculto) || Boolean(json.data.produto?.ocultar)
        );
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          setError(e?.message || "Falha ao buscar produto");
          setPayload(null);
          setIsOculto(false);
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [codigo]
  );

  React.useEffect(() => {
    if (!codigo) return;

    const abort = new AbortController();

    fetchProduto(abort.signal);

    return () => abort.abort();
  }, [codigo, fetchProduto]);

  const invalidateProdutosCache = React.useCallback(async () => {
    try {
      const r = await fetch(
        `${PRODUTOS_API_BASE}/departamentos/suprimentos/produtos/invalidate`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
          cache: "no-store",
        }
      );

      await r.text().catch(() => "");
    } catch {
      // não quebra a ação principal
    }
  }, []);

  const precosVendaArr: ApiPrecoVendaEntry[] = Array.isArray(
    payload?.precos_venda
  )
    ? [...payload!.precos_venda!]
    : [];

  precosVendaArr.sort((a, b) => {
    const ta = Date.parse(String(a.data_definicao || ""));
    const tb = Date.parse(String(b.data_definicao || ""));

    return tb - ta;
  });

  const custoReferenciaGlobal = parseNumberFlexible(
    payload?.estoque_atual?.custo_unitario_atual ?? null
  );

  const historicoPrecosVenda: SellHistItem[] = precosVendaArr.map((p) => {
    const precoVendaNum = parseNumberFlexible(p.preco_venda);

    const veioMargem = Number.isFinite(Number(p.margem_percentual))
      ? Number(p.margem_percentual)
      : null;

    const margemCalc =
      veioMargem == null &&
      Number.isFinite(precoVendaNum) &&
      Number.isFinite(custoReferenciaGlobal) &&
      custoReferenciaGlobal > 0
        ? ((precoVendaNum - custoReferenciaGlobal) / custoReferenciaGlobal) *
          100
        : null;

    const vendaMoeda = payload?.estoque_atual?.moeda ?? "BRL";

    return {
      id: p.id,
      produto_id: payload?.produto?.id ?? 0,
      movimentacao_id: undefined,
      tipo: "Ajuste",
      custo_unitario: Number.isFinite(precoVendaNum) ? precoVendaNum : null,
      quantidade: null,
      data_movimentacao: p.data_definicao,
      responsavel_id: p.definido_por_id,
      responsavel_nome: p.definido_por_nome ?? null,
      margem_mov_percentual: veioMargem ?? margemCalc,
      margem_percentual: veioMargem ?? margemCalc,
      preco_venda: Number.isFinite(precoVendaNum) ? precoVendaNum : null,
      custo_referencia: Number.isFinite(custoReferenciaGlobal)
        ? custoReferenciaGlobal
        : null,
      moeda: vendaMoeda,
    };
  });

  const movimentosOrdenados = React.useMemo(() => {
    const mov = payload?.movimentos_por_tipo;

    if (!mov) return [] as ApiMovItem[];

    type ApiMovRaw = ApiMovItem & { tipo_full?: string | null };

    const all: ApiMovRaw[] = Object.values(mov).flatMap(
      (arr) => arr ?? []
    ) as ApiMovRaw[];

    const withoutAjustePreco = all.filter((i) => !isAjustePreco(i));

    const mapped: ApiMovItem[] = withoutAjustePreco.map((i) => {
      const moedaItem = i.moeda ?? payload?.estoque_atual?.moeda ?? "BRL";

      const raw =
        (i as any).custo_unitario ??
        (i as any).custo ??
        (i as any).preco ??
        (i as any).valor ??
        (i as any).preco_unitario ??
        null;

      const valorFmt = raw == null ? "—" : formatMoneyByCode(raw, moedaItem);

      return {
        ...i,
        custo_unitario: valorFmt,
        custo: valorFmt,
        ...(isAjusteGenerico(i) ? { tipo: "Ajuste" } : {}),
      } as ApiMovItem;
    });

    return mapped.sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;

      return tb - ta;
    });
  }, [payload?.movimentos_por_tipo, payload?.estoque_atual?.moeda]);

  const precosCompraItems: ApiMovItem[] = React.useMemo(() => {
    const base: ApiMovItem[] = (payload?.custos ?? []).map((c: any) =>
      withCustoFields(c, c?.moeda ?? payload?.estoque_atual?.moeda)
    );

    const mov = payload?.movimentos_por_tipo;

    const ajustes: ApiMovItem[] = (() => {
      if (!mov) return [];

      const all = Object.values(mov).flatMap((arr) => arr ?? []) as any[];

      return all
        .filter((i) => isAjustePreco(i))
        .map((i) =>
          withCustoFields(
            i,
            i?.moeda ?? payload?.estoque_atual?.moeda,
            "Ajuste de Preço"
          )
        );
    })();

    const seen = new Set<string>();

    const keyOf = (x: any) =>
      `${x.data_movimentacao ?? ""}|${x.custo ?? x.custo_unitario ?? ""}|${
        x.responsavel_id ?? ""
      }`;

    const out: ApiMovItem[] = [];

    const pushUnique = (x: ApiMovItem) => {
      const k = keyOf(x);

      if (!seen.has(k)) {
        seen.add(k);
        out.push(x);
      }
    };

    base.forEach(pushUnique);
    ajustes.forEach(pushUnique);

    out.sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;

      return tb - ta;
    });

    return out;
  }, [
    payload?.custos,
    payload?.movimentos_por_tipo,
    payload?.estoque_atual?.moeda,
  ]);

  const custoUnitarioDisplay = (() => {
    const moAtual = payload?.estoque_atual?.moeda ?? null;
    const atualVal = payload?.estoque_atual?.custo_unitario_atual ?? null;
    const atualFmt = formatMoneyByCode(atualVal, moAtual);

    const ultimoItem = payload?.custos?.[0];
    const moUltimo = ultimoItem?.moeda ?? moAtual ?? null;

    const ultimoFmt = ultimoItem
      ? formatMoneyByCode((ultimoItem as any).custo_unitario as any, moUltimo)
      : "—";

    if (atualFmt === "—" && ultimoFmt === "—") return "—";
    if (ultimoFmt === "—") return `${atualFmt}`;

    return `${atualFmt} / ${ultimoFmt}`;
  })();

  const custoUnitarioNumber = (() => {
    const raw =
      payload?.estoque_atual?.custo_unitario_atual ??
      payload?.custos?.[0]?.custo_unitario;

    const n = parseNumberFlexible(raw);

    return Number.isFinite(n) ? n : NaN;
  })();

  const quantidadeEstoque = (() => {
    const q = payload?.estoque_atual?.quantidade_atual;

    return q == null ? "—" : String(q);
  })();

  const marginPctNumber = parseNumberFlexible(marginInput);
  const priceNumber = parseNumberFlexible(priceInput);

  const priceFromMargin = (() => {
    if (
      !Number.isFinite(custoUnitarioNumber) ||
      !Number.isFinite(marginPctNumber)
    ) {
      return NaN;
    }

    if (String(marginInput).trim() === "") return NaN;

    return custoUnitarioNumber * (1 + marginPctNumber / 100);
  })();

  const marginFromPrice = (() => {
    if (
      !Number.isFinite(custoUnitarioNumber) ||
      !Number.isFinite(priceNumber) ||
      String(priceInput).trim() === ""
    ) {
      return NaN;
    }

    if (priceNumber <= 0) return NaN;

    return ((priceNumber - custoUnitarioNumber) / custoUnitarioNumber) * 100;
  })();

  const vendavelAtual = !!payload?.produto?.vendavel;

  const precoVendaAtualNum: number | null = (() => {
    const n = parseNumberFlexible(
      payload?.preco_venda ?? precosVendaArr[0]?.preco_venda ?? null
    );

    return Number.isFinite(n) ? n : null;
  })();

  const vendaMoeda = payload?.estoque_atual?.moeda ?? "BRL";

  const precoVendaUltimoNum: number | null =
    precosVendaArr.length >= 2 &&
    Number.isFinite(parseNumberFlexible(precosVendaArr[1].preco_venda))
      ? parseNumberFlexible(precosVendaArr[1].preco_venda)
      : null;

  const precoVendaAtualFmt =
    precoVendaAtualNum != null
      ? formatMoneyByCode(precoVendaAtualNum, vendaMoeda)
      : "—";

  const precoVendaUltimoFmt =
    precoVendaUltimoNum != null
      ? formatMoneyByCode(precoVendaUltimoNum, vendaMoeda)
      : "—";

  const margemAtualPct: number | null =
    precosVendaArr[0]?.margem_percentual != null
      ? Number(precosVendaArr[0].margem_percentual)
      : null;

  if (!codigo) return <Navigate to="/suprimentos/produtos" replace />;

  // ====================== Ações ======================
  const handleToggleVendavel = async () => {
    if (!payload?.produto?.codigo) return;

    setVendavelErr(null);
    setVendavelMsg(null);

    const nextVendavel = !vendavelAtual;
    const ncmAtual = payload?.produto?.ncm || "";

    try {
      setVendavelPosting(true);

      const resp = await fetch(`${PRODUTOS_API_BASE}/produto-vendavel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({ codigo: payload.produto.codigo }),
      });

      const txt = await resp.text().catch(() => "");

      let j: any = null;

      try {
        j = txt ? JSON.parse(txt) : null;
      } catch {}

      if (!resp.ok || j?.ok === false) {
        const msg =
          j?.erro ||
          `Falha ao atualizar vendável (HTTP ${resp.status}) ${
            txt || ""
          }`.trim();

        throw new Error(msg);
      }

      setPayload((prev) =>
        prev
          ? {
              ...prev,
              produto: {
                ...prev.produto,
                ncm: ncmAtual,
                vendavel: nextVendavel,
              },
            }
          : prev
      );

      setVendavelMsg(
        nextVendavel
          ? "Produto definido como Vendável."
          : "Produto definido como Não Vendável."
      );

      await invalidateProdutosCache();
      await fetchProduto();
    } catch (e: any) {
      setVendavelErr(e?.message || "Falha ao atualizar status de venda.");
    } finally {
      setVendavelPosting(false);
    }
  };

  const handleOpenMargin = () => {
    setMarginError(null);
    setMarginSuccess(null);
    setMarginPosting(false);
    setShowMargin(true);
  };

  const handleCancelMargin = () => {
    if (marginPosting) return;

    setShowMargin(false);
    setMarginError(null);
    setMarginSuccess(null);
  };

  const handleConfirmMargin = async () => {
    if (!payload?.produto?.id) {
      setMarginError("Produto inválido.");
      return;
    }

    if (authLoading) {
      setMarginError("Sessão ainda carregando. Aguarde alguns segundos.");
      return;
    }

    if (!responsavelId) {
      await refreshAuth().catch(() => undefined);

      setMarginError(
        "Sessão não disponível para identificar o responsável pelo ajuste."
      );
      return;
    }

    const markupDigitado = parseNumberFlexible(marginInput);
    const precoDigitado = parseNumberFlexible(priceInput);
    const custoNum = custoUnitarioNumber;

    if (!Number.isFinite(custoNum) || custoNum <= 0) {
      setMarginError("Custo unitário indisponível ou inválido.");
      return;
    }

    const markupFromPriceCalc = (() => {
      if (!Number.isFinite(precoDigitado)) return NaN;
      if (String(priceInput).trim() === "" || precoDigitado <= 0) return NaN;

      return ((precoDigitado - custoNum) / custoNum) * 100;
    })();

    let markupPercentual: number | null = null;

    if (String(marginInput).trim() !== "" && Number.isFinite(markupDigitado)) {
      if (markupDigitado < 0 || markupDigitado > 1000) {
        setMarginError("Informe um markup válido (0 a 1000%).");
        return;
      }

      markupPercentual = markupDigitado;
    } else if (
      String(priceInput).trim() !== "" &&
      Number.isFinite(precoDigitado)
    ) {
      const m = markupFromPriceCalc;

      if (!Number.isFinite(m) || m < 0 || m > 1000) {
        setMarginError(
          "Preço informado gera markup fora do intervalo (0–1000%)."
        );
        return;
      }

      markupPercentual = m;
    } else {
      setMarginError("Preencha Markup (%) ou Preço de Venda.");
      return;
    }

    try {
      setMarginPosting(true);
      setMarginError(null);
      setMarginSuccess(null);

      const resp = await fetch(
        `${PRODUTOS_API_BASE}/departamentos/diretoria/ajustar-margem`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-store",
          body: JSON.stringify({
            produto_id: payload.produto.id,
            definido_por_id: responsavelId,
            margem_percentual: Number(markupPercentual.toFixed(4)),
          }),
        }
      );

      const txt = await resp.text().catch(() => "");

      let j: any = null;

      try {
        j = txt ? JSON.parse(txt) : null;
      } catch {}

      if (!resp.ok || j?.ok === false) {
        const msg =
          j?.erro ||
          `Falha ao ajustar markup (HTTP ${resp.status}) ${txt || ""}`.trim();

        throw new Error(msg);
      }

      setMarginSuccess("Markup registrado com sucesso.");

      await invalidateProdutosCache();
      await fetchProduto();
    } catch (e: any) {
      setMarginError(e?.message || "Falha ao ajustar markup.");
    } finally {
      setMarginPosting(false);
    }
  };

  // ====================== Estilos ======================
  const toggleWrapStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    padding: "0 40px",
    margin: "8px 0 12px 0",
    alignItems: "center",
    flexWrap: "wrap",
  };

  const baseBtn: React.CSSProperties = {
    padding: "8px 14px",
    borderRadius: 4,
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #ccc",
    background: "#124280",
    color: "#fff",
    outline: "none",
    userSelect: "none",
    transition:
      "transform 0.02s ease-in-out, box-shadow 0.1s ease-in-out, opacity 0.12s",
  };

  const btnActive: React.CSSProperties = {
    ...baseBtn,
    boxShadow: "0 0 0 1px rgba(18,66,128,0.15)",
    opacity: 1,
  };

  const btnInactive: React.CSSProperties = {
    ...baseBtn,
    opacity: 0.7,
  };

  const sellStatusChip: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 2,
    background: vendavelAtual ? "#e6ffed" : "#ffecec",
    color: vendavelAtual ? "#0f5132" : "#842029",
    border: `1px solid ${vendavelAtual ? "#badbcc" : "#f5c2c7"}`,
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
  };

  const outlineBlueBtn: React.CSSProperties = {
    padding: "5px 15px",
    borderRadius: 2,
    border: vendavelAtual ? "1.5px solid #801212" : "1.5px solid #128021",
    background: "#fff",
    color: vendavelAtual ? "#801212" : "#03491B",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  };

  return (
    <div>
      <TitleButtons
        fullBleedLine
        outerMarginLeft={40}
        outerMarginRight={20}
        title={`Diretoria - Detalhes do Produto / ${codigo}`}
        buttonMarginRight="0px"
        buttons={[
          {
            label: "Ajustar markup (sobre o custo)",
            onClick: handleOpenMargin,
            variant: "primary",
          },
        ]}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 8,
          padding: "0 40px",
          margin: "12px 0 20px 0",
        }}
      >
        <div style={sellStatusChip}>
          {vendavelAtual ? "Vendável: Ativo" : "Vendável: Inativo"}
        </div>

        <button
          type="button"
          onClick={handleToggleVendavel}
          disabled={vendavelPosting || !payload?.produto}
          aria-busy={vendavelPosting}
          title={
            vendavelAtual
              ? "Tornar NÃO vendável (desativar)"
              : "Tornar vendável (ativar)"
          }
          style={{ ...outlineBlueBtn, opacity: vendavelPosting ? 0.6 : 1 }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#eef4ff")
          }
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
          onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
          onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          {vendavelPosting
            ? "Salvando..."
            : vendavelAtual
            ? "Tornar não vendável"
            : "Tornar vendável"}
        </button>

        {(vendavelPosting || vendavelMsg || vendavelErr) && (
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {vendavelPosting && (
              <span style={{ color: "#64748b" }}>Atualizando…</span>
            )}

            {vendavelMsg && (
              <span style={{ color: "#0f5132" }}>{vendavelMsg}</span>
            )}

            {vendavelErr && (
              <span style={{ color: "#b02a37" }}>{vendavelErr}</span>
            )}
          </div>
        )}
      </div>

      {isOculto && (
        <div
          role="alert"
          style={{
            margin: "12px 40px 0 40px",
            padding: "10px 12px",
            borderRadius: 8,
            background: "#e7f1ff",
            color: "#084298",
            border: "1px solid #b6d4fe",
          }}
        >
          Este produto está marcado como <strong>oculto</strong>. Ele não
          aparecerá nas listagens padrão.
        </div>
      )}

      {showMargin && (
        <div
          style={{
            margin: "12px 40px 0 40px",
            padding: 16,
            borderRadius: 12,
            border: "1px solid #e6e8ec",
            background: "#fff",
            boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
          }}
        >
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 15,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 12,
            }}
          >
            Ajuste de Markup (sobre o custo)
          </div>

          {!Number.isFinite(custoUnitarioNumber) ? (
            <div
              role="alert"
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#ffecec",
                color: "#842029",
                border: "1px solid #f5c2c7",
              }}
            >
              Custo unitário indisponível — não é possível calcular markup.
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <label
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  Custo unitário atual
                </label>

                <div
                  style={{
                    height: 38,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    background: "#f8fafc",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    color: "#0f172a",
                  }}
                  aria-live="polite"
                >
                  {formatMoneyByCode(
                    payload?.estoque_atual?.custo_unitario_atual ?? null,
                    payload?.estoque_atual?.moeda ?? null
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <label
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#334155",
                    }}
                  >
                    Markup (%)
                  </label>

                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 25"
                    style={{
                      height: 38,
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "0 12px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                    }}
                    value={marginInput}
                    onChange={(e) => setMarginInput(e.target.value)}
                    disabled={
                      !Number.isFinite(custoUnitarioNumber) || marginPosting
                    }
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <label
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#334155",
                    }}
                  >
                    Preço de venda (
                    {currencySymbol(payload?.estoque_atual?.moeda) ||
                      payload?.estoque_atual?.moeda ||
                      "BRL"}
                    )
                  </label>

                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 199,90"
                    style={{
                      height: 38,
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "0 12px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                    }}
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    disabled={
                      !Number.isFinite(custoUnitarioNumber) || marginPosting
                    }
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={handleConfirmMargin}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #114280",
                    background: "#fff",
                    color: "#114280",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  disabled={
                    marginPosting ||
                    authLoading ||
                    !Number.isFinite(custoUnitarioNumber)
                  }
                  aria-busy={marginPosting || authLoading}
                >
                  {marginPosting
                    ? "Registrando…"
                    : authLoading
                    ? "Carregando sessão…"
                    : "Confirmar ajuste de markup"}
                </button>

                <button
                  type="button"
                  onClick={handleCancelMargin}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#f1f5f9",
                    color: "#0f172a",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  disabled={marginPosting}
                >
                  Cancelar
                </button>

                {marginError && (
                  <span
                    style={{
                      color: "#b02a37",
                      marginLeft: 8,
                      fontSize: 13,
                      alignSelf: "center",
                    }}
                  >
                    {marginError}
                  </span>
                )}

                {marginSuccess && (
                  <span
                    style={{
                      color: "#0f5132",
                      marginLeft: 8,
                      fontSize: 13,
                      alignSelf: "center",
                    }}
                  >
                    {marginSuccess}
                  </span>
                )}
              </div>
            </div>

            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                background: "#f8fafc",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                Resumo
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  color: "#0f172a",
                }}
              >
                <div style={{ opacity: 0.7 }}>Custo</div>

                <div style={{ textAlign: "right" }}>
                  {formatMoneyByCode(
                    payload?.estoque_atual?.custo_unitario_atual ?? null,
                    payload?.estoque_atual?.moeda ?? null
                  )}
                </div>

                <div style={{ opacity: 0.7 }}>Markup (%)</div>

                <div style={{ textAlign: "right" }}>
                  {String(marginInput).trim() !== "" &&
                  Number.isFinite(marginPctNumber)
                    ? `${marginPctNumber.toFixed(2)}%`
                    : Number.isFinite(marginFromPrice)
                    ? `${marginFromPrice.toFixed(2)}%`
                    : "—"}
                </div>

                <div style={{ opacity: 0.7 }}>Preço de venda</div>

                <div
                  style={{
                    textAlign: "right",
                    fontWeight: 700,
                  }}
                >
                  {String(marginInput).trim() !== "" &&
                  Number.isFinite(priceFromMargin)
                    ? formatMoneyByCode(priceFromMargin, vendaMoeda)
                    : Number.isFinite(priceNumber)
                    ? formatMoneyByCode(priceNumber, vendaMoeda)
                    : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          padding: "40px",
          gap: "100px",
          marginLeft: "0px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Nome do Produto
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading
                ? "Carregando..."
                : error
                ? "—"
                : payload?.produto?.nome ?? "—"}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Código do Produto
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {codigo}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Código NCM do Produto
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading
                ? "Carregando..."
                : error
                ? "—"
                : payload?.produto?.ncm ?? "—"}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Dimensão
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading
                ? "Carregando..."
                : error
                ? "—"
                : payload?.produto?.dimensao ?? "—"}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Peso
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading
                ? "Carregando..."
                : error
                ? "—"
                : payload?.produto?.peso
                ? `${payload.produto.peso}`
                : "—"}
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Posição do Produto
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading
                ? "Carregando..."
                : error
                ? "—"
                : payload?.produto?.posicao ?? "—"}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Custo Unitário (Atual / Último)
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading ? "Carregando..." : error ? "—" : custoUnitarioDisplay}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Preço de venda (Atual / Último)
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading
                ? "Carregando..."
                : error
                ? "—"
                : `${precoVendaAtualFmt}${
                    precoVendaUltimoNum != null
                      ? ` / ${precoVendaUltimoFmt}`
                      : ""
                  }`}
            </div>

            {!loading && !error && (
              <div
                style={{
                  marginTop: "6px",
                  fontFamily: "Inter",
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                Markup atual:{" "}
                <strong>
                  {margemAtualPct != null && Number.isFinite(margemAtualPct)
                    ? `${margemAtualPct.toFixed(2)}%`
                    : "—"}
                </strong>
              </div>
            )}
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                marginBottom: "4px",
              }}
            >
              Quantidade no Estoque
            </div>

            <div
              style={{
                fontFamily: "Inter",
                fontWeight: 300,
                fontSize: "14px",
              }}
            >
              {loading ? "Carregando..." : error ? "—" : quantidadeEstoque}
            </div>
          </div>

          {error && (
            <div
              style={{
                marginTop: "8px",
                color: "#c0362c",
                fontFamily: "Inter",
                fontSize: 13,
              }}
            >
              Erro ao carregar dados do produto: {error}
            </div>
          )}
        </div>
      </div>

      <div style={toggleWrapStyle} role="group" aria-label="Alternar tabela">
        <button
          type="button"
          onClick={() => setActiveTab("mov")}
          style={activeTab === "mov" ? btnActive : btnInactive}
          aria-pressed={activeTab === "mov"}
        >
          Histórico de Movimentações
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("precosCompra")}
          style={activeTab === "precosCompra" ? btnActive : btnInactive}
          aria-pressed={activeTab === "precosCompra"}
        >
          Histórico de Preços de Compra
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("precosVenda")}
          style={activeTab === "precosVenda" ? btnActive : btnInactive}
          aria-pressed={activeTab === "precosVenda"}
        >
          Histórico de Preços de Venda
        </button>
      </div>

      <div
        style={{
          padding: "0 40px",
          marginLeft: "0px",
          marginTop: "4px",
        }}
      >
        {activeTab === "mov" ? (
          loading ? (
            <div
              style={{
                padding: 16,
                opacity: 0.7,
                fontSize: 14,
              }}
            >
              Carregando histórico…
            </div>
          ) : movimentosOrdenados.length === 0 ? (
            <div
              style={{
                padding: 16,
                opacity: 0.7,
                fontSize: 14,
              }}
            >
              Sem movimentações para este produto.
            </div>
          ) : (
            <HistoricoWidget
              items={movimentosOrdenados}
              loading={loading}
              error={error}
              formatCurrency={(v) =>
                typeof v === "string" ? v : formatCurrency(v, "BRL")
              }
              formatDateTime={formatDateTime}
            />
          )
        ) : activeTab === "precosCompra" ? (
          <HistoricoPrecosWidget
            items={precosCompraItems}
            loading={loading}
            error={error}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
          />
        ) : (
          <HistoricoPrecosSellWidget
            items={historicoPrecosVenda}
            loading={loading}
            error={error}
            formatCurrency={(v) => formatCurrency(v, "BRL")}
            formatDateTime={formatDateTime}
          />
        )}
      </div>
    </div>
  );
};

export default DetalhesProdutoDiretoria;
